import { getApiErrorStatus } from '@/shared/services/api-error';
import { markReturningUser } from '@/shared/services/auth-presence';
import { decodeJwt } from '@/shared/services/jwt';
import {
  getUserToken,
  removeUserToken,
  setUserToken
} from '@/shared/services/user-hash';

import { ApiConnector } from './api.connector';
import type {
  IAuthSession,
  IAuthUser,
  IChangePasswordInput,
  ILoginInput,
  IPasswordResetInput,
  IPasswordResetOtpInput,
  IPasswordResetToken,
  IRegisterInput,
  ISsoAuthorization
} from './api.types/auth.api-types';

/**
 * Auth-эндпоинты super-app backend `/auth/*` и `/sso/authorize` (контракт delfi-mobile-api,
 * паритет с нативным @app/services/auth). Наследуется от ApiConnector и шлёт запросы через
 * общий `this.call` (единый стиль с другими веб-проектами). Исключение — `refresh`: идёт
 * напрямую через axios, чтобы 401 при ротации не дёрнул редирект-на-логин из `call`.
 */

interface ISsoAuthorizeResponse {
  authorizationCode: string;
  expiresInSeconds: number;
  clientId: string;
}

// === PKCE (S256) + deviceId — для sso.authorize, паритет с нативным sso-service ===

function base64Url(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let str = '';
  for (const b of arr) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generatePkcePair(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const random = new Uint8Array(32);
  crypto.getRandomValues(random);
  const codeVerifier = base64Url(random);
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier)
  );
  return { codeVerifier, codeChallenge: base64Url(digest) };
}

/** Claim'ы access-токена, которые кладём в сессию (паритет с нативным session-sync). */
interface IAccessTokenClaims {
  sub?: string;
  phone?: string;
  email?: string;
  name?: string;
}

/**
 * Профиль текущего пользователя из access-токена (sub→id, phone) — как нативный session-sync.
 * Источник правды для телефона в профиле. null, если токена / claim `sub` нет.
 */
export function sessionUserFromToken(): IAuthUser | null {
  const claims = decodeJwt<IAccessTokenClaims>(getUserToken().accessToken);
  if (!claims?.sub) return null;
  return {
    id: claims.sub,
    phone: claims.phone,
    email: claims.email,
    name: claims.name
  };
}

const DEVICE_ID_KEY = 'moni-device-id';

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

class AuthApi extends ApiConnector {
  /** `POST /auth/login` — вход по телефону + паролю; сохраняет пару токенов. */
  async login(input: ILoginInput): Promise<IAuthSession> {
    const session = await this.call<ILoginInput, IAuthSession>({
      path: 'auth/login',
      method: 'post',
      body: input,
      // 401 = неверные креды, не протухшая сессия — не редиректим (иначе форма перемонтируется).
      skipAuthRedirect: true
    });
    setUserToken({
      accessToken: session.accessToken,
      refreshToken: session.refreshToken
    });
    markReturningUser();
    return session;
  }

  /** `POST /auth/register/otp` — выслать OTP-код перед регистрацией. */
  async sendRegistrationOtp(phone: string): Promise<void> {
    await this.call<{ phone: string }, void>({
      path: 'auth/register/otp',
      method: 'post',
      body: { phone },
      skipAuthRedirect: true
    });
  }

  /** `POST /auth/register` — регистрация по телефону + OTP; сохраняет пару токенов. */
  async register({ ...rest }: IRegisterInput): Promise<IAuthSession> {
    const session = await this.call<IRegisterInput, IAuthSession>({
      path: 'auth/register',
      method: 'post',
      body: rest,
      // 401 (например, неверный OTP) не должен ронять форму через редирект.
      skipAuthRedirect: true
    });
    setUserToken({
      accessToken: session.accessToken,
      refreshToken: session.refreshToken
    });
    markReturningUser();
    return session;
  }

  /**
   * Восстановление пароля, шаг 1: `POST /auth/password/forgot` — выслать код на телефон
   * аккаунта. Сессию не создаёт и токены не трогает.
   */
  async sendPasswordResetOtp(phone: string): Promise<void> {
    await this.call<{ phone: string }, void>({
      path: 'auth/password/forgot',
      method: 'post',
      body: { phone },
      skipAuthRedirect: true
    });
  }

  /**
   * Восстановление пароля, шаг 2: `POST /auth/password/otp` — подтвердить код и получить
   * одноразовый токен сброса. Неверный код ловится здесь, до ввода нового пароля.
   */
  verifyPasswordResetOtp(
    input: IPasswordResetOtpInput
  ): Promise<IPasswordResetToken> {
    return this.call<IPasswordResetOtpInput, IPasswordResetToken>({
      path: 'auth/password/otp',
      method: 'post',
      body: input,
      // 401 = неверный или истёкший код, а не протухшая сессия.
      skipAuthRedirect: true
    });
  }

  /**
   * Восстановление пароля, шаг 3: `POST /auth/password/reset` — установить новый пароль
   * по токену сброса. Сессию не выдаёт: пользователь входит заново с новым паролем.
   */
  async resetPassword(input: IPasswordResetInput): Promise<void> {
    await this.call<IPasswordResetInput, unknown>({
      path: 'auth/password/reset',
      method: 'post',
      body: input,
      skipAuthRedirect: true
    });
  }

  /** `POST /auth/logout` — отзыв refresh-токена; очищает локальные токены. */
  async logout(): Promise<void> {
    const { refreshToken } = getUserToken();
    if (refreshToken) {
      try {
        await this.call<{ refreshToken: string }, void>({
          path: 'auth/logout',
          method: 'post',
          body: { refreshToken }
        });
      } catch {
        // отзыв на сервере не критичен для локального выхода
      }
    }
    removeUserToken();
  }

  /**
   * `POST /sso/authorize` (под суперапп-сессией) — одноразовый код для бэкенда сервиса.
   * PKCE S256 + deviceId; verifier возвращается рядом (web передаёт его своему бэкенду).
   */
  async ssoAuthorize(clientId: string): Promise<ISsoAuthorization> {
    if (!clientId) throw new Error('[auth] clientId не задан для SSO');
    const { codeVerifier, codeChallenge } = await generatePkcePair();
    const deviceId = getOrCreateDeviceId();

    const data = await this.call<unknown, ISsoAuthorizeResponse>({
      path: 'sso/authorize',
      method: 'post',
      body: {
        clientId,
        codeChallenge,
        codeChallengeMethod: 'S256',
        deviceId
      }
    });

    return {
      authorizationCode: data.authorizationCode,
      codeVerifier,
      expiresInSeconds: data.expiresInSeconds
    };
  }

  /**
   * `POST /auth/password/change` — смена пароля под текущей сессией, сессия сохраняется.
   * @param idempotencyKey один на попытку: повтор после обрыва связи идёт с тем же ключом,
   * иначе уже сменённый пароль вернулся бы ошибкой «неверный текущий пароль»
   */
  async changePassword(
    input: IChangePasswordInput,
    idempotencyKey: string
  ): Promise<void> {
    await this.ensureFreshSession();
    await this.call<IChangePasswordInput, unknown>({
      path: 'auth/password/change',
      method: 'post',
      body: input,
      extraHeaders: { 'idempotency-key': idempotencyKey },
      // 401 здесь может означать неверный текущий пароль: не должен выкидывать на вход.
      skipAuthRedirect: true
    });
  }

  /**
   * Авторизованный пользователь не помнит пароль: `POST /auth/password/recovery/otp` — код
   * на телефон аккаунта. 409 — первый запрос ещё выполняется, то есть SMS уже в пути.
   */
  async sendPasswordRecoveryOtp(): Promise<void> {
    await this.ensureFreshSession();
    try {
      await this.call<undefined, unknown>({
        path: 'auth/password/recovery/otp',
        method: 'post',
        skipAuthRedirect: true
      });
    } catch (err) {
      if (getApiErrorStatus(err) !== 409) throw err;
    }
  }

  /**
   * `POST /auth/password/recovery/otp/confirm` — проверить код и получить токен для
   * `resetPassword`. Проверка общая с `verifyPasswordResetOtp`, неверный код может быть 401.
   */
  async confirmPasswordRecoveryOtp(otp: string): Promise<IPasswordResetToken> {
    await this.ensureFreshSession();
    return this.call<{ otp: string }, IPasswordResetToken>({
      path: 'auth/password/recovery/otp/confirm',
      method: 'post',
      body: { otp },
      skipAuthRedirect: true
    });
  }
}

export const authApi = new AuthApi();
